import { type ReactNode } from "react";
import { X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

export type PillVariant = "muted" | "outlined" | "dashed" | "active" | "overlay";
export type PillSize = "sm" | "md";

export interface PillProps {
  children: ReactNode;
  variant?: PillVariant;
  size?: PillSize;
  onClick?: () => void;
  onRemove?: () => void;
  removeLabel?: string;
  href?: string;
  external?: boolean;
  showChevron?: boolean;
  chevronIcon?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** A toggle or scope choice: its on/off state for assistive tech (button pills only). */
  pressed?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}

const pillVariants = cva("inline-flex shrink-0 items-center rounded-full border font-medium gap-2 whitespace-nowrap", {
  variants: {
    variant: {
      muted: "bg-(--control-fill) border-(--control-line) text-foreground",
      outlined: "bg-(--control-fill) text-foreground border-border",
      dashed: "bg-(--control-fill) text-foreground border-dashed border-muted-foreground/40",
      active: "bg-foreground text-background border-foreground",
      overlay: "bg-black/50 text-white border-transparent backdrop-blur-sm",
    },
    size: { sm: "px-2.5 py-1 text-ui-caption", md: "px-3 py-1.5 text-ui-control" },
    interactive: { true: "min-h-(--control-height) min-w-(--control-height) cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-(--control-fill) disabled:border-(--control-line) disabled:text-muted-foreground" },
  },
  defaultVariants: { variant: "muted", size: "sm" },
});

/**
 * Pill - unified badge/chip/tag component
 *
 * Renders as:
 * - <span> for display-only (no onClick/href)
 * - <button> for interactive (onClick)
 * - <Link> for internal navigation (href without external)
 * - <a> for external links (href + external)
 */
export const Pill = ({
  children,
  variant = "muted",
  size = "sm",
  onClick,
  onRemove,
  removeLabel,
  href,
  external = false,
  showChevron = false,
  chevronIcon,
  className,
  style,
  pressed,
  disabled,
  ariaLabel,
}: PillProps) => {
  const isInteractive = onClick || href || onRemove;

  const combinedClassName = cn(
    pillVariants({ variant, size, interactive: !!isInteractive }),
    className,
  );

  const content = (
    <>
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel ?? (typeof children === "string" ? `Remove ${children}` : "Remove item")}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="[all:unset] cursor-pointer hover:opacity-70"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {showChevron && (chevronIcon || <ChevronDown className="h-4 w-4 opacity-50" />)}
    </>
  );

  // External link
  if (href && external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={combinedClassName}
        style={style}
      >
        {content}
      </a>
    );
  }

  // Internal link
  if (href) {
    return (
      <Link to={href} onClick={onClick} className={combinedClassName} style={style}>
        {content}
      </Link>
    );
  }

  // Button (interactive)
  if (onClick) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} aria-label={ariaLabel} aria-pressed={pressed} className={combinedClassName} style={style}>
        {content}
      </button>
    );
  }

  // Static span
  return (
    <span className={combinedClassName} style={style}>
      {content}
    </span>
  );
};
